/**
 * Phone-width clip guards for www leftovers at Scout 390 (iPhone 14 logical width).
 * Stack name fields and keep the tablist shrinkable so overflow-x-auto can scroll
 * instead of a parent overflow-x-hidden clipping labels.
 */

export const WWW_PHONE_WIDTH_PX = 390;

/**
 * Auth Sign In / Register column scales with the viewport: inset at Scout 390,
 * then grows on sm/lg instead of staying a 28rem island.
 */
export const AUTH_FORM_COLUMN_CLASS =
  'box-border w-full min-w-0 max-w-[min(28rem,calc(100vw-2rem))] sm:max-w-[min(36rem,90vw)] lg:max-w-[min(42rem,88vw)] mx-auto px-4';

export const AUTH_FORM_COLUMN_TESTID = 'auth-form-column';

/** Extra wordmark + subtitle above the auth card. Hidden when marketing nav already brands the page. */
export const AUTH_SHELL_BRAND_TESTID = 'auth-shell-brand';

export const AUTH_SIGN_IN_CARD_TESTID = 'auth-sign-in-card';

/**
 * Focus / scroll-into-view clearance under the top-pinned analytics banner
 * plus the marketing nav (h-14) at Scout 390.
 */
export const AUTH_PASSWORD_SCROLL_MARGIN_CLASS =
  'scroll-mt-[calc(3.5rem+env(safe-area-inset-top,0px)+1rem+var(--consent-banner-offset,0px))]';

export const AUTH_SHELL_ROOT_CLASS =
  'relative min-h-svh w-full max-w-full overflow-x-clip';

/**
 * WEB-JITTER-02 centering (`items-center justify-center`). First-paint height
 * is `min-h-svh` so the Sign In tile is not scaled. `--auth-shell-min-height`
 * from visualViewport then holds the stage when keyboard / autofill chrome
 * would otherwise shrink it and jump the card.
 */
export const AUTH_SHELL_STAGE_CLASS =
  'relative z-10 box-border flex min-h-svh w-full min-w-0 items-center justify-center py-8 sm:py-10';

export const AUTH_SHELL_STAGE_TESTID = 'auth-shell-stage';

export const AUTH_SHELL_STAGE_MIN_HEIGHT_STYLE = {
  minHeight: 'var(--auth-shell-min-height, 100svh)',
};

/**
 * Clears the persistent marketing nav (h-14 + safe area) at Scout 390.
 * Phone starts the card under the nav (`items-start`). Centering it in
 * `min-h-svh` left a large empty band on /login and /register.
 * `sm+` stays vertically centered. `--consent-banner-offset` is set while
 * the analytics banner sits under the nav so the password row is not covered.
 */
export const AUTH_SHELL_STAGE_WITH_NAV_CLASS =
  'relative z-10 box-border flex min-h-svh w-full min-w-0 items-start justify-center pt-[calc(3.5rem+env(safe-area-inset-top,0px)+1rem+var(--consent-banner-offset,0px))] pb-8 sm:items-center sm:pt-[calc(4rem+env(safe-area-inset-top,0px)+2.5rem+var(--consent-banner-offset,0px))] sm:pb-10';

/**
 * OAuth divider + chips: stacked 44px row at Scout 390, side-by-side from `sm`.
 * Hold this min-height on first paint and after config resolves so the
 * vertically centered Sign In card does not jump. Desktop uses the shorter
 * row height so the slot does not leave a blank band above the card footer.
 */
export const AUTH_OAUTH_SLOT_MIN_CLASS = 'min-h-[8.25rem] sm:min-h-[5rem]';

export const AUTH_OAUTH_SLOT_CLASS = `mt-6 space-y-3 ${AUTH_OAUTH_SLOT_MIN_CLASS}`;

export const AUTH_OAUTH_SLOT_TESTID = 'auth-oauth-slot';

/** Register first/last name row: one column below Tailwind `sm` (640px). */
export const REGISTER_NAME_GRID_CLASS =
  'grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2';

export const REGISTER_NAME_FIELD_CLASS = 'min-w-0';

export const REGISTER_NAME_INPUT_CLASS =
  'input-modern min-w-0 px-3 text-sm sm:px-4';

export const REGISTER_NAME_GRID_TESTID = 'register-name-fields';

/**
 * Settings page: clip horizontal leftovers only. `overflow-x-hidden` computes
 * overflow-y to `auto` and can trap Account Support below Quick Actions inside
 * a 100svh shell at Scout 390. `clip` + `visible` keeps #main-content scrolling.
 */
export const SETTINGS_PAGE_OVERFLOW_CLASS =
  'max-w-6xl mx-auto min-w-0 space-y-6 overflow-x-clip overflow-y-visible';

/**
 * Marketing landing/pricing footer at Scout 390. A wrapping flex row still lets
 * Privacy clip past both viewport edges (centered leftover). Two columns keep
 * every label inside the padded shell; sm+ returns to a wrapping row.
 */
export const MARKETING_FOOTER_SHELL_CLASS =
  'w-full min-w-0 max-w-full overflow-x-clip bg-[#1A1A1A] text-stone-300';

export const MARKETING_FOOTER_NAV_CLASS =
  'grid w-full min-w-0 grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-4';

export const MARKETING_FOOTER_NAV_TESTID = 'marketing-footer-nav';

export const MARKETING_FOOTER_LINK_CLASS =
  'inline-flex min-h-[44px] min-w-0 max-w-full items-center justify-start px-0 py-2 text-sm text-stone-400 hover:text-white transition-colors focus-visible:ring-offset-0';

/**
 * Public doc / legal reading column scales with the viewport instead of a fixed rem cap.
 * Phone top inset lives on `PUBLIC_MARKETING_PAGE_PAD_CLASS` (nav clearance only).
 * `pt-0` here so `py-12` does not stack a second band under the header.
 */
export const PUBLIC_DOC_MAIN_CLASS =
  'mx-auto w-full min-w-0 max-w-[min(72rem,94vw)] px-[max(1rem,4vw)] pb-12 pt-0 sm:py-16';

/** Account-tab Support card below Quick Actions. Do not add overflow-hidden. */
export const SETTINGS_ACCOUNT_SUPPORT_TESTID = 'settings-account-support';

export const SETTINGS_ACCOUNT_SUPPORT_CLASS = 'min-w-0 overflow-visible';

/** Sidebar stack (Launch access / Quick Actions / Support). */
export const SETTINGS_ACCOUNT_STACK_CLASS = 'space-y-6 min-w-0 overflow-visible';

export const SETTINGS_SUPPORT_SEND_CLASS =
  'w-full btn-primary btn-compact min-h-[44px] flex items-center justify-center gap-2';

/**
 * Tablist must shrink inside overflow-x-clip ancestors (Settings page)
 * so Integrations / Account can scroll into view instead of clipping mid-label.
 * Deep links like /settings?tab=account still need scrollTabIntoListView so the
 * active tab is fully visible at Scout 390 without shrinking 44px taps.
 */
export const TABS_LIST_OVERFLOW_CLASS =
  'flex min-w-0 w-full max-w-full overflow-x-auto scrollbar-hide';

export const TABS_ICON_VISIBILITY_CLASS = 'hidden shrink-0 sm:block';

export const TABS_MD_SIZE_CLASS =
  'px-2.5 py-2.5 text-sm gap-1.5 sm:px-4 sm:gap-2';

export const TABS_SM_SIZE_CLASS = 'px-3 py-2 text-xs gap-1.5';

/**
 * Scroll a horizontal tablist just enough that `tabEl` is fully visible.
 * Adjusts list scrollLeft only (does not scroll the page).
 */
export function scrollTabIntoListView(listEl, tabEl) {
  if (!listEl || !tabEl) return;

  const list = listEl.getBoundingClientRect();
  const tab = tabEl.getBoundingClientRect();
  if (!(list.width > 0) || !(tab.width > 0)) return;

  let delta = 0;
  if (tab.left < list.left) {
    delta = tab.left - list.left;
  } else if (tab.right > list.right) {
    delta = tab.right - list.right;
  }
  if (delta === 0) return;

  // Instant jump so /settings?tab=account is unclipped on first paint at 390.
  if (listEl.style) listEl.style.scrollBehavior = 'auto';
  listEl.scrollLeft += delta;
}
